import type { GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import * as React from 'react';
import { DEFAULT_PINNED_COLUMNS } from '../constants';
import { prunePinningEntries, readPinningEntry, writePinningEntry } from './tablePinningStorage';

const mergeWithDefaults = (cols: GridPinnedColumnFields): GridPinnedColumnFields => ({
  left: [...new Set([...(DEFAULT_PINNED_COLUMNS.left ?? []), ...(cols.left ?? [])])],
  right: [...new Set([...(DEFAULT_PINNED_COLUMNS.right ?? []), ...(cols.right ?? [])])],
});

export const useItemsTableColumnPinning = ({
  instanceKey,
}: {
  instanceKey: string | undefined;
}): {
  pinnedColumns: GridPinnedColumnFields;
  onPinnedColumnsChange: (cols: GridPinnedColumnFields) => void;
} => {
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumnFields>(() => {
    if (!instanceKey) return DEFAULT_PINNED_COLUMNS;
    const stored = readPinningEntry(instanceKey);
    return stored ? mergeWithDefaults(stored) : DEFAULT_PINNED_COLUMNS;
  });

  React.useEffect(() => {
    if (!instanceKey) return;
    prunePinningEntries();
    const stored = readPinningEntry(instanceKey);
    setPinnedColumns(stored ? mergeWithDefaults(stored) : DEFAULT_PINNED_COLUMNS);
  }, [instanceKey]);

  const onPinnedColumnsChange = React.useCallback(
    (next: GridPinnedColumnFields) => {
      if (!instanceKey) return;
      const merged = mergeWithDefaults(next);
      setPinnedColumns(merged);
      writePinningEntry(instanceKey, merged);
    },
    [instanceKey],
  );

  return { pinnedColumns, onPinnedColumnsChange };
};
